/**
 * The survey's steps in the DANI chat after it is written (docs/pdlc/builder.md,
 * "The whole survey in the chat"): save, check, preview, feasibility, price,
 * publish, launch and fielding, each as a card in the conversation.
 *
 * Which card comes next is Builder's own next step (readiness's `next`), never
 * a second rule: the chat only names the card that takes it. A survey not yet
 * saved has one step, Save.
 *
 * Pure.
 */
import type { NextAction, Readiness } from '../readiness';

export type FlowStep =
    /** Not saved yet: the draft card's Save. */
    | 'save'
    /** Problems, blanks or fielding numbers to settle: DANI changes the survey. */
    | 'check'
    /** No project chosen yet. */
    | 'project'
    | 'feasibility'
    | 'preview'
    | 'publish'
    | 'launch'
    /** Launched: Builder's part is done; the chat's status card follows fielding. */
    | 'fielding'
    /** Nothing this person can do in the chat: the reason is `waitingOn`. */
    | 'wait';

export interface FlowState {
    step: FlowStep;
    /** The card's button, in readiness's words. */
    label: string;
    /** The phase to preview, for a phased study. */
    phase?: number;
    /** Why the flow is waiting, when it is. */
    waitingOn?: string | null;
}

/** Launch states after which the survey is in the field or on its way there. */
const LAUNCHED = new Set(['fielding', 'in_review', 'sending']);

const BY_KIND: Record<NextAction['kind'], FlowStep> = {
    fix: 'check',
    blanks: 'check',
    fielding: 'check',
    project: 'project',
    check: 'feasibility',
    preview: 'preview',
    publish: 'publish',
    launch: 'launch',
};

/** The card the chat shows next for a saved survey, or Save for one that is not. */
export function chatFlowStep(input: {
    saved: boolean;
    ready: Readiness | null;
    launchStatus: string | null;
}): FlowState {
    if (!input.saved || !input.ready) return { step: 'save', label: 'Save' };
    const { next, waitingOn } = input.ready;
    if (next) {
        return {
            step: BY_KIND[next.kind],
            label: next.label,
            ...(next.kind === 'preview' ? { phase: next.phase } : {}),
        };
    }
    if (input.launchStatus && LAUNCHED.has(input.launchStatus))
        return { step: 'fielding', label: 'Launched' };
    return { step: 'wait', label: 'Waiting', waitingOn };
}

/** The steps as the chat's stepper names them, in the order a survey is built in the chat. */
export const FLOW_STEPS: readonly { step: FlowStep; label: string }[] = [
    { step: 'save', label: 'Save' },
    { step: 'check', label: 'Check' },
    { step: 'project', label: 'Project' },
    { step: 'feasibility', label: 'Feasibility' },
    { step: 'preview', label: 'Preview' },
    { step: 'publish', label: 'Publish' },
    { step: 'launch', label: 'Launch' },
    { step: 'fielding', label: 'Fielding' },
];

/**
 * How DANI speaks of a survey Builder can publish but feasibility says will not
 * finish (UAT F45: "0 of 500 expected", and "nothing is stopping you").
 */
export const FEASIBILITY_IS_SEPARATE =
    'the feasibility check answers that. When it says the survey will not reach its target (not feasible, or far fewer completes than wanted), lead with that and what would change it, and never say nothing stands in its way or nothing stops the person: publishing it would field a survey unlikely to finish.';

/**
 * What DANI is told, as a hidden message, when a step in the chat finishes or
 * is refused: what happened, in Studio's words, and the card that comes next.
 * DANI explains it and never claims a step happened that this does not say.
 */
export const SURVEY_STEP_HEADER = 'SURVEY STEP FROM STUDIO';

export function stepBriefForDani(args: {
    draftId: string;
    title: string;
    versionNo: number;
    /** What just happened, in one or two sentences. */
    what: string;
    next: FlowState;
    /** The problems that stop publishing, when the next step is to fix them. */
    problems?: readonly string[];
}): string {
    const { draftId, title, versionNo, what, next, problems } = args;
    const nextLine =
        next.step === 'check'
            ? `Next: ${next.label.toLowerCase()}. Ask the person what they want where only they can say (a number, a link), then write a survey-change block with "draftId": "${draftId}" and the fix in words.`
            : next.step === 'wait'
              ? `Nothing more to do in the chat for now: ${next.waitingOn ?? 'it is with the MFour team'}.`
              : next.step === 'fielding'
                ? `It is launched, and Builder's part is done. Whenever the person asks how it is fielding, in this chat or any other, write a survey-status block with "draftId": "${draftId}".`
                : `Next: ${next.label}. The card under this conversation offers it; to show it again, write a survey-next block with "draftId": "${draftId}".`;
    return [
        `${SURVEY_STEP_HEADER} (not from the person; they do not see this message)`,
        '',
        `Survey: ${title || 'Untitled survey'} (draftId ${draftId}, version ${versionNo}).`,
        what,
        ...(problems?.length
            ? [
                  'Problems that stop it from publishing:',
                  ...problems.slice(0, 20).map((p) => `- ${p}`),
              ]
            : []),
        nextLine,
        'Tell the person briefly, using only what is above.',
    ].join('\n');
}

/** The ask a card's "Fix with DANI" sends: DANI hands the fix to the survey author. */
export function fixAskForDani(args: {
    draftId: string;
    problems: readonly string[];
    missingFielding: boolean;
}): string {
    return [
        `${SURVEY_STEP_HEADER} (not from the person; they do not see this message)`,
        '',
        `The person asked you to fix what stops survey ${args.draftId} from publishing:`,
        ...args.problems.slice(0, 20).map((p) => `- ${p}`),
        ...(args.missingFielding
            ? [
                  'Some fielding numbers are not set (completes, field days or the share who qualify). Ask the person for them; never make them up.',
              ]
            : []),
        `When you have what you need, write a survey-change block with "draftId": "${args.draftId}" and the fix in words. Anything only the person can settle, such as a link, ask them first.`,
    ].join('\n');
}
